import i18n from '~/i18n';
import { parseISO } from 'date-fns';
import dateIsAfterDate from '~/lib/validators/dateIsAfterDate';

const dueDateValidator = (rule, value, formData, callback) => {
  if (!value || !formData.issuedOn) {
    return callback();
  }

  const issuedOn = typeof formData.issuedOn === 'string' ? parseISO(formData.issuedOn) : new Date(formData.issuedOn);
  const dueOn = typeof value === 'string' ? parseISO(value) : new Date(value);

  if (dateIsAfterDate(issuedOn, dueOn)) {
    return callback();
  }

  return callback(new Error('usa.form.invoiceForm.fields.dueOn.messages.beforeIssueDate'));
};

const getDefaultConfig = (formData = {}) => ({
  typeOfDocument: {
    title: i18n.t('usa.form.invoiceForm.section.documentTypes.title'),
  },
  basicInformation: {
    title: i18n.t('usa.form.invoiceForm.section.basicInfo.title'),
    seller: {
      label: i18n.t('usa.form.invoiceForm.fields.seller.label'),
      placeholder: i18n.t('usa.form.invoiceForm.fields.seller.placeholder'),
      visible: true,
      required: true,
      validations: [
        {
          required: true,
          message: 'usa.form.invoiceForm.fields.seller.messages.required',
          trigger: 'blur',
        },
      ],
    },
    buyer: {
      label: i18n.t('usa.form.invoiceForm.fields.buyer.label'),
      visible: true,
      placeholder: i18n.t('usa.form.invoiceForm.fields.buyer.placeholder'),
      validations: [
        {
          required: true,
          message: 'usa.form.invoiceForm.fields.buyer.messages.required',
          trigger: 'blur',
        },
      ],
    },
    shipTo: {
      label: i18n.t('usa.form.invoiceForm.fields.shipTo.label'),
      visible: true,
      placeholder: i18n.t('usa.form.invoiceForm.fields.shipTo.placeholder'),
    },
    issuedOn: {
      label: i18n.t('usa.form.invoiceForm.fields.issuedOn.label'),
      visible: true,
      validations: [
        {
          required: true,
          message: 'usa.form.invoiceForm.fields.issuedOn.messages.required',
          trigger: 'blur',
        },
        {
          type: 'date',
          message: 'usa.form.invoiceForm.fields.issuedOn.messages.invalid',
          trigger: 'blur',
        },
      ],
    },
    dueOn: {
      label: i18n.t('usa.form.invoiceForm.fields.dueOn.label'),
      visible: true,
      validations: [
        {
          type: 'date',
          message: 'usa.form.invoiceForm.fields.dueOn.messages.invalid',
          trigger: 'blur',
        },
        {
          validator: (rule, value, callback) => {
            dueDateValidator(rule, value, formData, callback);
          },
          trigger: 'blur',
        },
      ],
    },
    number: {
      label: i18n.t('usa.form.invoiceForm.fields.number.label'),
      visible: true,
      validations: [
        {
          required: true,
          message: 'usa.form.invoiceForm.fields.number.messages.invoice.required',
          trigger: 'blur',
        },
      ],
    },
    orderNumber: {
      label: i18n.t('usa.form.invoiceForm.fields.orderNumber.label'),
      visible: true,
    },
    currency: {
      label: i18n.t('usa.form.invoiceForm.fields.currency.label'),
      visible: true,
    },
    vatCalculation: {
      label: i18n.t('usa.form.invoiceForm.fields.vatCalculation.label'),
      visible: false,
      options: [
        {
          key: 'vat_exclusive',
          value: i18n.t('usa.form.invoiceForm.fields.vatCalculation.options.vatExclusive'),
        },
        {
          key: 'vat_inclusive',
          value: i18n.t('usa.form.invoiceForm.fields.vatCalculation.options.vatInclusive'),
        },
      ],
    },
    logo: {
      label: i18n.t('usa.form.invoiceForm.fields.logo.label'),
      visible: true,
    },
    paymentMethod: {
      visible: false,
    },
    paymentMethodCustom: {
      visible: false,
    },
  },
  invoiceItems: {
    title: i18n.t('usa.form.invoiceForm.section.invoiceItems.title'),
    description: {
      label: i18n.t('usa.form.invoiceForm.fields.description.label'),
      visible: true,
    },
    quantity: {
      label: i18n.t('usa.form.invoiceForm.fields.quantity.label'),
      visible: true,
    },
    unit: {
      visible: false,
    },
    price: {
      label: i18n.t('usa.form.invoiceForm.fields.price.label'),
      visible: true,
    },
    amount: {
      label: i18n.t('usa.form.invoiceForm.fields.amount.label'),
      visible: true,
    },
    tax: {
      label: i18n.t('usa.form.invoiceForm.fields.tax.label'),
      visible: true,
    },
    buttons: {
      duplicate: {
        visible: true,
      },
      note: {
        visible: true,
      },
      savedItem: {
        visible: true,
      },
    },
  },
  noteAndSignature: {
    notes: {
      label: i18n.t('usa.form.invoiceForm.fields.notes.label'),
      visible: true,
    },
    stamp: {
      label: i18n.t('usa.form.invoiceForm.fields.stamp.label'),
      visible: true,
    },
  },
  vatCalculation: {
    visible: false,
  },
  summary: {
    subtotal: {
      visible: true,
    },
    items: {
      visible: true,
    },
  },
});

const formConfigs = {
  default: (formData = {}) => ({ ...getDefaultConfig(formData) }),
  invoice: (formData = {}) => ({ ...getDefaultConfig(formData) }),
  receipt: (formData = {}) => ({
    ...getDefaultConfig(formData),
    basicInformation: {
      ...getDefaultConfig(formData).basicInformation,
      issuedOn: {
        label: i18n.t('usa.form.invoiceForm.fields.receiptIssuedOn.label'),
        visible: true,
        validations: [
          {
            required: true,
            message: 'usa.form.invoiceForm.fields.issuedOn.messages.receipt.required',
            trigger: 'blur',
          },
          {
            type: 'date',
            message: 'usa.form.invoiceForm.fields.issuedOn.messages.receipt.invalid',
            trigger: 'blur',
          },
        ],
      },
      number: {
        label: i18n.t('usa.form.invoiceForm.fields.receiptNumber.label'),
        visible: true,
        validations: [
          {
            required: true,
            message: 'usa.form.invoiceForm.fields.number.messages.receipt.required',
            trigger: 'blur',
          },
        ],
      },
      dueOn: {
        visible: false,
      },
    },
    invoiceItems: {
      ...getDefaultConfig(formData).invoiceItems,
      title: i18n.t('usa.form.invoiceForm.section.invoiceItems.receiptTitle'),
    },
  }),
  salesReceipt: (formData = {}) => ({
    ...getDefaultConfig(formData),
    basicInformation: {
      ...getDefaultConfig(formData).basicInformation,
      buyer: {
        ...getDefaultConfig(formData).basicInformation.buyer,
        label: i18n.t('usa.form.invoiceForm.fields.salesReceiptBuyer.label'),
      },
      issuedOn: {
        label: i18n.t('usa.form.invoiceForm.fields.receiptIssuedOn.label'),
        visible: true,
        validations: [
          {
            required: true,
            message: 'usa.form.invoiceForm.fields.issuedOn.messages.salesReceipt.required',
            trigger: 'blur',
          },
          {
            type: 'date',
            message: 'usa.form.invoiceForm.fields.issuedOn.messages.salesReceipt.invalid',
            trigger: 'blur',
          },
        ],
      },
      number: {
        label: i18n.t('usa.form.invoiceForm.fields.receiptNumber.label'),
        visible: true,
        validations: [
          {
            required: true,
            message: 'usa.form.invoiceForm.fields.number.messages.salesReceipt.required',
            trigger: 'blur',
          },
        ],
      },
      dueOn: {
        visible: false,
      },
    },
    invoiceItems: {
      ...getDefaultConfig(formData).invoiceItems,
      title: i18n.t('usa.form.invoiceForm.section.invoiceItems.salesReceiptTitle'),
    },
  }),
  cashReceipt: (formData = {}) => {
    const defaultConfig = getDefaultConfig(formData);
    return {
      ...defaultConfig,
      basicInformation: {
        ...defaultConfig.basicInformation,
        seller: {
          ...defaultConfig.basicInformation.seller,
          label: i18n.t('usa.form.invoiceForm.fields.cashReceiptSeller.label'),
        },
        buyer: {
          ...defaultConfig.basicInformation.buyer,
          label: i18n.t('usa.form.invoiceForm.fields.cashReceiptBuyer.label'),
        },
        dueOn: {
          visible: false,
        },
        issuedOn: {
          label: i18n.t('usa.form.invoiceForm.fields.cashReceiptIssuedOn.label'),
          visible: true,
          validations: [
            {
              required: true,
              message: 'usa.form.invoiceForm.fields.issuedOn.messages.cashReceipt.required',
              trigger: 'blur',
            },
            {
              type: 'date',
              message: 'usa.form.invoiceForm.fields.issuedOn.messages.cashReceipt.invalid',
              trigger: 'blur',
            },
          ],
        },
        number: {
          label: i18n.t('usa.form.invoiceForm.fields.cashReceiptNumber.label'),
          visible: true,
          validations: [
            {
              required: true,
              message: 'usa.form.invoiceForm.fields.number.messages.cashReceipt.required',
              trigger: 'blur',
            },
          ],
        },
        orderNumber: {
          visible: false,
        },
        shipTo: {
          visible: false,
        },
        paymentMethod: {
          label: i18n.t('usa.form.invoiceForm.fields.paymentMethod.label'),
          visible: true,
        },
        paymentMethodCustom: {
          label: i18n.t('usa.form.invoiceForm.fields.paymentMethodCustom.label'),
          visible: true,
        },
      },
      invoiceItems: {
        ...defaultConfig.invoiceItems,
        title: i18n.t('usa.form.invoiceForm.section.invoiceItems.cashReceiptTitle'),
        amount: {
          visible: false,
        },
        description: {
          label: i18n.t('usa.form.invoiceForm.fields.cashReceiptDescription.label'),
          visible: true,
        },
        quantity: {
          visible: false,
        },
        tax: {
          visible: false,
        },
        buttons: {
          duplicate: {
            visible: false,
          },
          note: {
            visible: false,
          },
          savedItem: {
            visible: false,
          },
        },
      },
      noteAndSignature: {
        ...defaultConfig.noteAndSignature,
        notes: {
          visible: false,
        },
      },
      summary: {
        subtotal: {
          visible: false,
        },
        items: {
          visible: false,
        },
      },
    };
  },
  quote: (formData = {}) => ({
    ...getDefaultConfig(formData),
    basicInformation: {
      ...getDefaultConfig(formData).basicInformation,
      issuedOn: {
        label: i18n.t('usa.form.invoiceForm.fields.quoteIssuedOn.label'),
        visible: true,
        validations: [
          {
            required: true,
            message: 'usa.form.invoiceForm.fields.issuedOn.messages.quote.required',
            trigger: 'blur',
          },
          {
            type: 'date',
            message: 'usa.form.invoiceForm.fields.issuedOn.messages.quote.invalid',
            trigger: 'blur',
          },
        ],
      },
      number: {
        label: i18n.t('usa.form.invoiceForm.fields.quoteNumber.label'),
        visible: true,
        validations: [
          {
            required: true,
            message: 'usa.form.invoiceForm.fields.number.messages.quote.required',
            trigger: 'blur',
          },
        ],
      },
      dueOn: {
        visible: false,
      },
    },
    invoiceItems: {
      ...getDefaultConfig(formData).invoiceItems,
      title: i18n.t('usa.form.invoiceForm.section.invoiceItems.quoteTitle'),
    },
  }),
  estimate: (formData = {}) => ({
    ...getDefaultConfig(formData),
    basicInformation: {
      ...getDefaultConfig(formData).basicInformation,
      issuedOn: {
        label: i18n.t('usa.form.invoiceForm.fields.estimateIssuedOn.label'),
        visible: true,
        validations: [
          {
            required: true,
            message: 'usa.form.invoiceForm.fields.issuedOn.messages.estimate.required',
            trigger: 'blur',
          },
          {
            type: 'date',
            message: 'usa.form.invoiceForm.fields.issuedOn.messages.estimate.invalid',
            trigger: 'blur',
          },
        ],
      },
      number: {
        label: i18n.t('usa.form.invoiceForm.fields.estimateNumber.label'),
        visible: true,
        validations: [
          {
            required: true,
            message: 'usa.form.invoiceForm.fields.number.messages.estimate.required',
            trigger: 'blur',
          },
        ],
      },
      dueOn: {
        visible: false,
      },
    },
    invoiceItems: {
      ...getDefaultConfig(formData).invoiceItems,
      title: i18n.t('usa.form.invoiceForm.section.invoiceItems.estimateTitle'),
    },
  }),
  creditMemo: (formData = {}) => ({
    ...getDefaultConfig(formData),
    basicInformation: {
      ...getDefaultConfig(formData).basicInformation,
      buyer: {
        ...getDefaultConfig(formData).basicInformation.buyer,
        label: i18n.t('usa.form.invoiceForm.fields.creditMemoBuyer.label'),
      },
      issuedOn: {
        label: i18n.t('usa.form.invoiceForm.fields.creditMemoIssuedOn.label'),
        visible: true,
        validations: [
          {
            required: true,
            message: 'usa.form.invoiceForm.fields.issuedOn.messages.creditMemo.required',
            trigger: 'blur',
          },
          {
            type: 'date',
            message: 'usa.form.invoiceForm.fields.issuedOn.messages.creditMemo.invalid',
            trigger: 'blur',
          },
        ],
      },
      number: {
        label: i18n.t('usa.form.invoiceForm.fields.creditMemoNumber.label'),
        visible: true,
        validations: [
          {
            required: true,
            message: 'usa.form.invoiceForm.fields.number.messages.creditMemo.required',
            trigger: 'blur',
          },
        ],
      },
      dueOn: {
        label: i18n.t('usa.form.invoiceForm.fields.dueOn.label'),
        visible: true,
        validations: [
          {
            type: 'date',
            message: 'usa.form.invoiceForm.fields.dueOn.messages.invalid',
            trigger: 'blur',
          },
        ],
      },
    },
    invoiceItems: {
      ...getDefaultConfig(formData).invoiceItems,
      title: i18n.t('usa.form.invoiceForm.section.invoiceItems.creditMemoTitle'),
    },
  }),
  creditNote: (formData = {}) => ({
    ...getDefaultConfig(formData),
    basicInformation: {
      ...getDefaultConfig(formData).basicInformation,
      buyer: {
        ...getDefaultConfig(formData).basicInformation.buyer,
        label: i18n.t('usa.form.invoiceForm.fields.creditNoteBuyer.label'),
      },
      issuedOn: {
        label: i18n.t('usa.form.invoiceForm.fields.creditNoteIssuedOn.label'),
        visible: true,
        validations: [
          {
            required: true,
            message: 'usa.form.invoiceForm.fields.issuedOn.messages.creditNote.required',
            trigger: 'blur',
          },
          {
            type: 'date',
            message: 'usa.form.invoiceForm.fields.issuedOn.messages.creditNote.invalid',
            trigger: 'blur',
          },
        ],
      },
      number: {
        label: i18n.t('usa.form.invoiceForm.fields.creditNoteNumber.label'),
        visible: true,
        validations: [
          {
            required: true,
            message: 'usa.form.invoiceForm.fields.number.messages.creditNote.required',
            trigger: 'blur',
          },
        ],
      },
      dueOn: {
        label: i18n.t('usa.form.invoiceForm.fields.dueOn.label'),
        visible: true,
        validations: [
          {
            type: 'date',
            message: 'usa.form.invoiceForm.fields.dueOn.messages.invalid',
            trigger: 'blur',
          },
        ],
      },
    },
    invoiceItems: {
      ...getDefaultConfig(formData).invoiceItems,
      title: i18n.t('usa.form.invoiceForm.section.invoiceItems.creditNoteTitle'),
    },
  }),
  purchaseOrder: (formData = {}) => ({
    ...getDefaultConfig(formData),
    basicInformation: {
      ...getDefaultConfig(formData).basicInformation,
      buyer: {
        ...getDefaultConfig(formData).basicInformation.buyer,
        label: i18n.t('usa.form.invoiceForm.fields.purchaseOrderBuyer.label'),
      },
      issuedOn: {
        label: i18n.t('usa.form.invoiceForm.fields.purchaseOrderIssuedOn.label'),
        visible: true,
        validations: [
          {
            required: true,
            message: 'usa.form.invoiceForm.fields.issuedOn.messages.purchaseOrder.required',
            trigger: 'blur',
          },
          {
            type: 'date',
            message: 'usa.form.invoiceForm.fields.issuedOn.messages.purchaseOrder.invalid',
            trigger: 'blur',
          },
        ],
      },
      number: {
        label: i18n.t('usa.form.invoiceForm.fields.purchaseOrderNumber.label'),
        visible: true,
        validations: [
          {
            required: true,
            message: 'usa.form.invoiceForm.fields.number.messages.purchaseOrder.required',
            trigger: 'blur',
          },
        ],
      },
      dueOn: {
        visible: false,
      },
      orderNumber: {
        visible: false,
      },
    },
    invoiceItems: {
      ...getDefaultConfig(formData).invoiceItems,
      title: i18n.t('usa.form.invoiceForm.section.invoiceItems.purchaseOrderTitle'),
    },
  }),
  deliveryNote: (formData = {}) => ({
    ...getDefaultConfig(formData),
    basicInformation: {
      ...getDefaultConfig(formData).basicInformation,
      seller: {
        ...getDefaultConfig(formData).basicInformation.seller,
        label: i18n.t('usa.form.invoiceForm.fields.deliveryNoteSeller.label'),
      },
      buyer: {
        ...getDefaultConfig(formData).basicInformation.buyer,
        label: i18n.t('usa.form.invoiceForm.fields.deliveryNoteBuyer.label'),
      },
      issuedOn: {
        label: i18n.t('usa.form.invoiceForm.fields.deliveryNoteIssuedOn.label'),
        visible: true,
        validations: [
          {
            required: true,
            message: 'usa.form.invoiceForm.fields.deliveryNote.messages.deliveryNote.required',
            trigger: 'blur',
          },
          {
            type: 'date',
            message: 'usa.form.invoiceForm.fields.deliveryNote.messages.deliveryNote.invalid',
            trigger: 'blur',
          },
        ],
      },
      number: {
        label: i18n.t('usa.form.invoiceForm.fields.deliveryNoteNumber.label'),
        visible: true,
        validations: [
          {
            required: true,
            message: 'usa.form.invoiceForm.fields.number.messages.deliveryNote.required',
            trigger: 'blur',
          },
        ],
      },
      dueOn: {
        label: i18n.t('usa.form.invoiceForm.fields.dueOn.label'),
        visible: true,
        validations: [
          {
            type: 'date',
            message: 'usa.form.invoiceForm.fields.dueOn.messages.invalid',
            trigger: 'blur',
          },
        ],
      },
    },
    invoiceItems: {
      ...getDefaultConfig(formData).invoiceItems,
      title: i18n.t('usa.form.invoiceForm.section.invoiceItems.deliveryNoteTitle'),
    },
  }),
};

export default formConfigs;
